'use client';

import { useMemo } from 'react';
import { ChatItem } from './ChatItem';
import { cn } from '@/src/shared/lib/utils';
import { useChatStore } from '../model/useChatStore';
import type { ChatFilter, Conversation } from '../types';

const ACTIVE_ORDER_STATUSES = new Set(['ASSIGNED', 'IN_PROGRESS', 'PENDING_REVIEW']);
const COMPLETED_ORDER_STATUSES = new Set(['COMPLETED', 'CANCELLED', 'DISPUTED']);

function filterConversations(
  conversations: Conversation[],
  filter: ChatFilter
): Conversation[] {
  switch (filter) {
    case 'Active':
      return conversations.filter(
        (c) =>
          c.activeOrder && ACTIVE_ORDER_STATUSES.has(c.activeOrder.status)
      );
    case 'Completed':
      return conversations.filter(
        (c) =>
          c.activeOrder && COMPLETED_ORDER_STATUSES.has(c.activeOrder.status)
      );
    case 'Unread':
      return conversations.filter((c) => c.unreadCount > 0);
    default:
      return conversations;
  }
}

interface ChatListProps {
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  filter?: ChatFilter;
  className?: string;
}

export const ChatList = ({
  selectedConversationId,
  onSelectConversation,
  filter = 'All',
  className,
}: ChatListProps) => {
  const { conversations } = useChatStore();

  const filtered = useMemo(
    () => filterConversations(conversations, filter),
    [conversations, filter]
  );

  // Sort: unread first, then by last message time (newest first)
  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return bTime - aTime;
      }),
    [filtered]
  );

  if (conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center text-text-secondary">
          <p className="mb-2">No conversations</p>
          <p className="text-sm">Your conversations will appear here</p>
        </div>
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center text-text-secondary">
          <p className="mb-2">No conversations match the filter</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col', className)}>
      <div className="flex-1 overflow-y-auto">
        {sorted.map((conversation) => (
          <ChatItem
            key={conversation.id}
            id={conversation.id}
            name={
              conversation.otherParticipant?.displayName ||
              conversation.otherParticipant?.name ||
              'User'
            }
            orderTitle={conversation.activeOrder?.title}
            orderStatus={conversation.activeOrder?.status}
            lastMessage={conversation.lastMessage?.body || 'No messages yet'}
            time={
              conversation.lastMessageAt
                ? formatTime(conversation.lastMessageAt)
                : ''
            }
            avatar={conversation.otherParticipant?.avatarUrl || undefined}
            unreadCount={conversation.unreadCount}
            isSelected={selectedConversationId === conversation.id}
            onClick={onSelectConversation}
          />
        ))}
      </div>
    </div>
  );
};

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
